import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Check, Sparkles, Zap, Crown, Loader2, ArrowRight,
  Copy, CheckCheck, QrCode, X, Clock
} from "lucide-react";

export default function PricingPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [yearly, setYearly] = useState(false);
  const [busyPlan, setBusyPlan] = useState(null);
  const [pixModal, setPixModal] = useState(null); // { qr_code, qr_code_base64, session_id, plan_key, amount }

  useEffect(() => {
    api.get("/billing/plans").then((r) => setPlans(r.data.plans));
  }, []);

  // Polling de status enquanto o modal está aberto
  useEffect(() => {
    if (!pixModal) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/billing/status/${pixModal.session_id}`);
        if (data.status === "complete") {
          clearInterval(interval);
          setPixModal(null);
          await refreshUser();
          toast.success(`Plano ${data.plan_key} ativado com sucesso! 🎉`);
          navigate("/painel");
        }
      } catch {}
    }, 4000);
    return () => clearInterval(interval);
  }, [pixModal]);

  const subscribe = async (planKey) => {
    if (user?.is_admin) {
      toast.info("Você é admin — já tem acesso ilimitado!");
      return;
    }
    setBusyPlan(planKey);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_key: planKey,
        cycle: yearly ? "yearly" : "monthly",
        origin_url: window.location.origin,
      });
      setPixModal({
        qr_code: data.qr_code,
        qr_code_base64: data.qr_code_base64,
        session_id: data.session_id,
        plan_key: data.plan_key,
        amount: data.amount,
      });
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao gerar Pix"));
    } finally {
      setBusyPlan(null);
    }
  };

  const ICONS = { free: Sparkles, pro: Zap, agency: Crown };
  const formatBRL = (n) =>
    n === 0 ? "Grátis" : `R$ ${n.toFixed(2).replace(".", ",")}`;

  return (
    <div className="p-6 md:p-12 max-w-[1300px] mx-auto">
      <div className="text-center mb-10">
        <p className="font-mono-label text-blue-600 mb-2">Planos & Preços</p>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
          Escolha o plano ideal para você
        </h1>
        <p className="text-zinc-500 mt-3 max-w-xl mx-auto">
          Comece grátis. Faça upgrade quando precisar de mais grupos, agendamento e recursos avançados.
        </p>

        <div className="mt-8 inline-flex items-center gap-3 bg-zinc-100 rounded-sm p-1.5">
          <button
            onClick={() => setYearly(false)}
            className={`px-4 py-1.5 rounded-sm text-sm font-medium transition-colors ${
              !yearly ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-600"
            }`}
          >
            Mensal
          </button>
          <button
            onClick={() => setYearly(true)}
            className={`px-4 py-1.5 rounded-sm text-sm font-medium transition-colors inline-flex items-center gap-2 ${
              yearly ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-600"
            }`}
          >
            Anual
            <Badge className="bg-green-100 text-green-700 border-0 rounded-sm font-mono-label">
              -20%
            </Badge>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => {
          const Icon = ICONS[plan.key] || Sparkles;
          const price = yearly ? plan.yearly_price : plan.monthly_price;
          const monthly = yearly ? plan.yearly_price / 12 : plan.monthly_price;
          const isCurrent = user?.plan_key === plan.key && !user?.is_admin;
          const isHighlight = plan.highlight;

          return (
            <div
              key={plan.key}
              className={`relative bg-white border-2 rounded-sm p-7 flex flex-col ${
                isHighlight
                  ? "border-blue-600 shadow-lg shadow-blue-100"
                  : "border-zinc-200"
              }`}
            >
              {isHighlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-3 py-0.5 font-mono-label rounded-sm">
                  MAIS POPULAR
                </div>
              )}

              <div className="flex items-center gap-3 mb-2">
                <div className={`h-10 w-10 rounded-sm flex items-center justify-center ${
                  plan.key === "free" ? "bg-zinc-100 text-zinc-600"
                  : plan.key === "pro" ? "bg-blue-50 text-blue-600"
                  : "bg-amber-50 text-amber-600"
                }`}>
                  <Icon className="h-5 w-5" strokeWidth={2} />
                </div>
                <div>
                  <div className="font-display text-xl font-bold">{plan.name}</div>
                  <div className="text-xs text-zinc-500">{plan.tagline}</div>
                </div>
              </div>

              <div className="mt-6 mb-6">
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-4xl font-bold tracking-tight">
                    {formatBRL(yearly ? monthly : price)}
                  </span>
                  {price > 0 && <span className="text-zinc-500 text-sm">/mês</span>}
                </div>
                {yearly && price > 0 && (
                  <p className="text-xs text-zinc-500 mt-1">
                    Cobrado {formatBRL(price)} por ano
                  </p>
                )}
                {!yearly && price === 0 && (
                  <p className="text-xs text-zinc-500 mt-1">Para sempre, sem cartão</p>
                )}
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                {plan.features.map((f, i) => {
                  const excluido = f.toLowerCase().startsWith("x ");
                  const texto = excluido ? f.slice(2) : f;
                  return (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      {excluido ? (
                        <X className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                      ) : (
                        <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                      )}
                      <span className={excluido ? "text-zinc-400 line-through" : "text-zinc-700"}>{texto}</span>
                    </li>
                  );
                })}
              </ul>

              {plan.key === "free" ? (
                <Button
                  variant="outline"
                  disabled={isCurrent}
                  onClick={() => navigate("/painel")}
                  className="rounded-sm h-11"
                >
                  {isCurrent ? "Plano atual" : "Começar grátis"}
                </Button>
              ) : (
                <Button
                  onClick={() => subscribe(plan.key)}
                  disabled={busyPlan === plan.key || isCurrent || user?.is_admin}
                  className={`rounded-sm h-11 font-medium ${
                    isHighlight
                      ? "bg-blue-600 hover:bg-blue-700 text-white"
                      : "bg-zinc-900 hover:bg-zinc-800 text-white"
                  }`}
                >
                  {busyPlan === plan.key ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  {isCurrent ? "Plano atual"
                    : user?.is_admin ? "Acesso admin"
                    : "Assinar via Pix"}
                  {!busyPlan && !isCurrent && !user?.is_admin && (
                    <ArrowRight className="h-4 w-4 ml-2" />
                  )}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-12 bg-zinc-50 border border-zinc-200 rounded-sm p-6 text-center">
        <h3 className="font-display text-lg font-semibold mb-2">Pagamento via Pix</h3>
        <p className="text-sm text-zinc-600 max-w-2xl mx-auto">
          Pagamento 100% seguro via Pix. O acesso é liberado automaticamente em menos de 1 minuto após a confirmação do pagamento.
        </p>
      </div>

      {/* Modal Pix */}
      {pixModal && (
        <PixModal
          {...pixModal}
          onClose={() => setPixModal(null)}
        />
      )}
    </div>
  );
}

function PixModal({ qr_code, qr_code_base64, amount, plan_key, onClose }) {
  const [copied, setCopied] = useState(false);
  const [seconds, setSeconds] = useState(300); // 5 min

  useEffect(() => {
    const t = setInterval(() => setSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, []);

  const copy = () => {
    navigator.clipboard.writeText(qr_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
    toast.success("Código Pix copiado!");
  };

  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");
  const formatBRL = (n) => `R$ ${n.toFixed(2).replace(".", ",")}`;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm w-full max-w-md p-6 relative shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-700"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="text-center mb-6">
          <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <QrCode className="h-6 w-6 text-green-600" />
          </div>
          <h2 className="font-display text-2xl font-bold">Pague via Pix</h2>
          <p className="text-zinc-500 text-sm mt-1">
            Plano <strong className="capitalize">{plan_key}</strong> — {formatBRL(amount)}
          </p>
        </div>

        {/* QR Code */}
        {qr_code_base64 && (
          <div className="flex justify-center mb-4">
            <img
              src={`data:image/png;base64,${qr_code_base64}`}
              alt="QR Code Pix"
              className="w-48 h-48 border border-zinc-200 rounded-sm"
            />
          </div>
        )}

        {/* Copia e Cola */}
        <div className="mb-4">
          <p className="text-xs font-mono-label text-zinc-500 mb-2">PIX COPIA E COLA</p>
          <div className="flex gap-2">
            <div className="flex-1 bg-zinc-50 border border-zinc-200 rounded-sm px-3 py-2 text-xs font-mono text-zinc-600 truncate">
              {qr_code}
            </div>
            <button
              onClick={copy}
              className="flex-shrink-0 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-sm transition-colors"
            >
              {copied
                ? <CheckCheck className="h-4 w-4" />
                : <Copy className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* Timer e status */}
        <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-zinc-600">
            <Clock className="h-4 w-4" />
            <span>Expira em <strong>{mm}:{ss}</strong></span>
          </div>
          <div className="flex items-center gap-2 text-sm text-green-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Aguardando pagamento...</span>
          </div>
        </div>

        <p className="text-xs text-zinc-400 text-center mt-4">
          Após o pagamento, o acesso é liberado automaticamente. Não feche essa janela.
        </p>
      </div>
    </div>
  );
}
